import { useDispatch, useSelector } from 'react-redux';

import {
  getAllModules,
  getReviewModule,
  getReviewModules,
  moduleCommentAdded,
  moduleCommentDeleted,
  moduleCommentUpdated,
  reviewModuleAdded,
  reviewModulesLoaded,
  reviewModuleStatusUpdated,
  revisionAdded,
  revisionDeleted,
  revisionModulesLoaded,
  revisionUpdated,
} from 'features/reviewModules/reviewModules.slice';

import { apiAction } from 'shared/actions/api';
import { RESOURCES } from 'shared/config/resourceNames';
import { API_REVISION_ROUTES } from 'shared/config/routes/api/apiRevisionRoutes';
import { API_SUBMISSION_MODULE_ROUTES } from 'shared/config/routes/api/apiSubmissionModuleRoutes';
import { useComponentLoading } from 'shared/hooks/useComponentLoading';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { ApiOnSuccess } from 'shared/typings/api';
import { ModuleComment } from 'shared/typings/moduleComments';
import { Revision, RevisionStatus } from 'shared/typings/revisions';
import { ModuleSubmissionStatus, ReviewModule } from 'shared/typings/submissions';

export const useReviewModules = (id?: number) => {
  const reviewModules: ReviewModule[] = useSelector(getReviewModules);
  const allModules: ReviewModule[] = useSelector(getAllModules);
  const reviewModule: ReviewModule = useSelector(getReviewModule(id));
  const dispatch = useDispatch();
  const { isSuperadmin } = useCurrentUser();

  const { loading } = useComponentLoading(RESOURCES.REVIEW_MODULE, true);

  // Helper function to check if a revision can be edited/deleted (only unaddressed revisions)
  const canEditRevision = (revision: Revision): boolean => {
    return !!isSuperadmin && revision.status === RevisionStatus.DOING;
  };

  const fetchReviewModules = (briefId: number, submissionId?: number) => {
    // Fetch both review and revision modules
    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.REVIEW_MODULES,
          query: {
            briefId,
            submissionId,
          },
        },
        successAction: reviewModulesLoaded,
        entity: RESOURCES.REVIEW_MODULE,
      }),
    );

    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.REVISION_MODULES,
          query: {
            briefId,
            submissionId,
          },
        },
        successAction: revisionModulesLoaded,
        entity: RESOURCES.REVIEW_MODULE,
      }),
    );
  };

  const updateSubmissionModuleStatus = ({
    submissionModuleId,
    status,
    onSuccess,
    onError,
  }: {
    submissionModuleId: number;
    status: ModuleSubmissionStatus;
    onSuccess?: (args) => void;
    onError?: (args) => void;
  }) => {
    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.SUBMISSION_MODULES,
          variables: {
            submissionModuleId,
          },
        },
        method: 'PATCH',
        data: {
          status,
        },
        onSuccess,
        onError,
        successAction: reviewModuleStatusUpdated,
        entity: RESOURCES.SUBMISSION_MODULE,
      }),
    );
  };

  const createModuleComment = ({
    submissionModuleId,
    moduleComment,
    onFinish,
  }: {
    submissionModuleId: number;
    moduleComment: Partial<ModuleComment>;
    onFinish?: () => void;
  }) => {
    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.MODULE_COMMENTS,
          variables: {
            submissionModuleId,
          },
        },
        data: moduleComment,
        method: 'POST',
        successAction: moduleCommentAdded,
        entity: RESOURCES.MODULE_COMMENT,
        onSuccess: onFinish,
        onError: onFinish,
      }),
    );
  };

  const updateModuleComment = (
    commentId: number,
    submissionModuleId: number,
    comment: string,
    onFinish: () => void,
  ) => {
    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.MODULE_COMMENT,
          variables: {
            submissionModuleId,
            commentId,
          },
        },
        data: {
          comment,
        },
        method: 'PATCH',
        successAction: moduleCommentUpdated,
        onSuccess: onFinish,
        onError: onFinish,
      }),
    );
  };

  const deleteModuleComment = (commentId: number, submissionModuleId: number, onFinish: () => void) => {
    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.MODULE_COMMENT,
          variables: {
            commentId,
            submissionModuleId,
          },
        },
        method: 'DELETE',
        successAction: moduleCommentDeleted,
        onSuccess: onFinish,
        onError: onFinish,
      }),
    );
  };

  const addReviewModule = (
    submissionModuleId: number,
    briefId: number,
    onSuccess?: ApiOnSuccess,
    onError?: () => void,
  ) => {
    dispatch(
      apiAction({
        path: {
          route: API_SUBMISSION_MODULE_ROUTES.REVIEW_MODULE,
          variables: {
            submissionModuleId,
          },
          query: {
            briefId,
          },
        },
        successAction: reviewModuleAdded,
        onSuccess,
        onError,
      }),
    );
  };

  const createRevision = (
    revision: Pick<Revision, 'category' | 'comment' | 'submissionModuleId'>,
    creatorId: number,
    moduleStatus: ModuleSubmissionStatus,
    onSuccess?: ApiOnSuccess,
    onError?: () => void,
  ) => {
    dispatch(
      apiAction({
        path: {
          route: API_REVISION_ROUTES.REVISIONS,
        },
        method: 'POST',
        params: {
          moduleStatus,
        },
        data: {
          ...revision,
          creatorId,
          moduleStatus,
        },
        successAction: revisionAdded,
        onSuccess,
        onError,
      }),
    );
  };

  const updateRevision = (
    revisionId: number,
    revision: Pick<Revision, 'category' | 'comment'>,
    submissionModuleId?: number,
    onSuccess?: ApiOnSuccess,
    onError?: () => void,
  ) => {
    dispatch(
      apiAction({
        path: {
          route: API_REVISION_ROUTES.REVISION_UPDATE,
          variables: {
            revisionId,
          },
        },
        method: 'PATCH',
        data: {
          ...revision,
          submissionModuleId,
        },
        successAction: revisionUpdated,
        onSuccess,
        onError,
      }),
    );
  };

  const deleteRevision = (
    revisionId: number,
    submissionModuleId?: number,
    onSuccess?: ApiOnSuccess,
    onError?: () => void,
  ) => {
    dispatch(
      apiAction({
        path: {
          route: API_REVISION_ROUTES.REVISION,
          variables: {
            revisionId,
          },
        },
        method: 'DELETE',
        data: {
          submissionModuleId,
        },
        successAction: revisionDeleted,
        onSuccess: (response) => {
          if (response.data?.submissionModule) {
            dispatch(reviewModuleStatusUpdated({ data: response.data.submissionModule }));
          }
          // Call the original onSuccess if provided
          if (onSuccess) {
            onSuccess(response);
          }
        },
        onError,
      }),
    );
  };

  return {
    addReviewModule,
    canEditRevision,
    createModuleComment,
    createRevision,
    deleteModuleComment,
    deleteRevision,
    fetchReviewModules,
    loading,
    reviewModule,
    reviewModules,
    allModules,
    updateModuleComment,
    updateRevision,
    updateSubmissionModuleStatus,
  };
};
